import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Mail, Wrench } from 'lucide-react';
import { useState } from 'react';
import { config, supabaseConfigured } from '../config.ts';
import { services } from '../services/index.ts';
import { errorMessage, toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Logo } from '../ui/Logo.tsx';

/** Sign in: Apple, Google, email code (Supabase Auth); "Dev sign-in" in local builds. */
export function SignInScreen() {
  const navigate = useNavigate();
  const { next } = useSearch({ from: '/signin' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [devEmail, setDevEmail] = useState('dev@example.com');
  const sb = supabaseConfigured();

  const done = () => navigate({ to: next ?? '/account', replace: true });
  const run = async (key: string, fn: () => Promise<unknown>, after?: () => unknown) => {
    setBusy(key);
    try {
      await fn();
      await after?.();
    } catch (e) {
      toast(errorMessage(e, 'Sign-in failed.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center px-2">
        <IconButton
          icon={ArrowLeft}
          label="Back"
          onClick={() => (history.length > 1 ? history.back() : void navigate({ to: '/' }))}
        />
      </div>
      <div className="mx-auto flex max-w-[400px] flex-col gap-5 p-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <Logo width={48} />
          <h1 className="t-title">Sign in to Better Lights</h1>
          <p className="t-body text-dim">
            Sync projects across your devices and create photoreal AI renders. Everything else works without an account.
          </p>
        </div>
        {config.oauthProviders.includes('apple') ? (
          <Button
            full
            size="lg"
            disabled={!sb || !!busy}
            loading={busy === 'apple'}
            onClick={() => void run('apple', () => services.account.signInWithProvider('apple'))}
          >
            Continue with Apple
          </Button>
        ) : null}
        {config.oauthProviders.includes('google') ? (
          <Button
            full
            size="lg"
            disabled={!sb || !!busy}
            loading={busy === 'google'}
            onClick={() => void run('google', () => services.account.signInWithProvider('google'))}
          >
            Continue with Google
          </Button>
        ) : null}
        {config.oauthProviders.length > 0 ? (
          <div className="flex items-center gap-3 text-muted">
            <span className="h-px flex-1 bg-border" />
            <span className="t-caption">or with email</span>
            <span className="h-px flex-1 bg-border" />
          </div>
        ) : null}
        {!sent ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                'send',
                () => services.account.sendEmailCode(email),
                () => setSent(true),
              );
            }}
          >
            <Field
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
            <Button type="submit" full icon={Mail} disabled={!sb || !email || !!busy} loading={busy === 'send'}>
              Email me a code
            </Button>
          </form>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void run('verify', () => services.account.verifyEmailCode(email, code), done);
            }}
          >
            <Field
              label={`Code sent to ${email}`}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
            />
            <Button
              type="submit"
              variant="primary"
              full
              disabled={code.trim().length < 6 || !!busy}
              loading={busy === 'verify'}
            >
              Sign in
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSent(false)}>
              Use a different email
            </Button>
          </form>
        )}
        {!sb ? (
          <p className="t-caption text-center text-muted">
            Apple, Google and email sign-in aren’t configured in this build.
          </p>
        ) : null}
        {config.devAuth ? (
          <form
            className="flex flex-col gap-3 rounded-md border border-dashed border-border-strong p-3"
            onSubmit={(e) => {
              e.preventDefault();
              void run('dev', () => services.account.devSignIn(devEmail), done);
            }}
          >
            <span className="t-overline text-muted">Dev sign-in (local server only)</span>
            <Field label="Email" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} />
            <Button type="submit" icon={Wrench} full loading={busy === 'dev'} disabled={!!busy || !devEmail}>
              Dev sign-in
            </Button>
          </form>
        ) : null}
        <p className="t-caption text-center text-muted">
          By continuing you agree to the{' '}
          <Link to="/terms" className="underline">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
